import { ReactNode } from 'react';
import { Accordion } from 'components/DesignSystems/Accordion/Accordion';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { useUscgMapContext } from './UscgMapContext';
import { useCreateLocation } from 'hooks/icsLocation/useCreateLocation';
import { LayerFeaturesEditor } from './LayerFeaturesEditor';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InternalLayer } from 'hooks/internalLayer/types';
import { PointGeometry } from './utils/constants';
import { NULL_VALUE } from 'constants/displayConstants';
import { IconButton } from 'components/DesignSystems/buttons/IconButton';

export function PropertyRow({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  return (
    <div className="d-flex gap-2 small">
      <span className="text-muted">{label}:</span>
      <span>{value ?? NULL_VALUE}</span>
    </div>
  );
}

function LayerHeader({
  title,
  layerId,
  layerType,
  error,
  onOpenInNew,
  onZoomToLayer,
  onDelete,
}: {
  title: ReactNode;
  layerId: string;
  layerType: 'internal' | 'external';
  error?: string;
  onOpenInNew?: () => void;
  onZoomToLayer?: () => void;
  onDelete?: () => void;
}) {
  const {
    isInternalLayerVisible,
    isExternalLayerVisible,
    toggleInternalLayerVisibility,
    toggleExternalLayerVisibility,
  } = useUscgMapContext();

  const isVisible =
    layerType === 'internal'
      ? isInternalLayerVisible(layerId)
      : isExternalLayerVisible(layerId);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (layerType === 'internal') {
      toggleInternalLayerVisibility(layerId);
    } else {
      toggleExternalLayerVisibility(layerId);
    }
  };

  return (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex align-items-center gap-2">
        <StylishNewCheckbox
          className="mt-0"
          checked={isVisible}
          onClick={handleToggle}
        />
        <span className="fw-semibold">{title}</span>
      </div>
      <div className="d-flex gap-1 align-items-center">
        {layerType === 'external' && (
          <>
            <div
              className="text-success rounded py-1 px-2 border border-success"
              style={{ background: 'rgba(34, 197, 94, 0.125)' }}
            >
              ArcGIS
            </div>
            {error && (
              <OverlayTrigger
                placement="top"
                overlay={(props) => <Tooltip {...props}>{error}</Tooltip>}
              >
                <IconButton
                  iconProps={{
                    iconName: 'error',
                    color: 'danger',
                  }}
                  aria-label="Error message"
                  title="Error message"
                />
              </OverlayTrigger>
            )}
          </>
        )}
        {onZoomToLayer && (
          <IconButton
            iconProps={{ iconName: 'map' }}
            onClick={(e) => {
              e.stopPropagation();
              onZoomToLayer();
            }}
            aria-label="Zoom to layer"
            title="Zoom to layer"
          />
        )}
        {onDelete && (
          <IconButton
            iconProps={{ iconName: 'delete' }}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            aria-label="Delete layer"
            title="Delete layer"
            data-testid="delete-layer-btn"
          />
        )}
        {onOpenInNew && (
          <IconButton
            iconProps={{ iconName: 'open_in_new' }}
            onClick={(e) => {
              e.stopPropagation();
              onOpenInNew();
            }}
            aria-label="View layer details"
            title="View layer details"
          />
        )}
      </div>
    </div>
  );
}

export function FeatureDetailRows({
  lastUpdated,
  point,
}: {
  lastUpdated?: string | null;
  point?: PointGeometry;
}) {
  return (
    <>
      <PropertyRow label="Last Updated" value={lastUpdated} />
      <PropertyRow label="Latitude" value={point?.latitude.toString()} />
      <PropertyRow label="Longitude" value={point?.longitude.toString()} />
    </>
  );
}

type LayerFeaturesListProps = {
  isLoading?: boolean;
  internalLayer?: InternalLayer | null;
  showAddObjectButton?: boolean;
  children?: ReactNode;
};

function LayerFeaturesList({
  isLoading,
  children,
  internalLayer,
  showAddObjectButton,
}: LayerFeaturesListProps) {
  const createLocation = useCreateLocation();

  if (isLoading) {
    return (
      <p className="mb-0 small text-muted p-2 pt-0">Loading features...</p>
    );
  }

  return (
    <>
      <LayerFeaturesEditor
        internalLayer={internalLayer}
        showAddObjectButton={showAddObjectButton}
        onSubmitLocation={async ({ location }) => {
          await createLocation.mutateAsync(location);
        }}
      />
      {children}
    </>
  );
}

export const Layer = {
  Item: ({
    eventKey,
    header,
    children,
    cardClassName = '',
  }: {
    eventKey: string;
    header: ReactNode;
    children: ReactNode;
    cardClassName?: string;
  }) => (
    <li className="opacity-hover-reveal position-relative list-unstyled">
      <Accordion
        classes={{ card: classNames('border-neutral', cardClassName) }}
        eventKey={eventKey}
        header={header}
      >
        {children}
      </Accordion>
    </li>
  ),
  Header: LayerHeader,
  Content: ({
    children,
    className = '',
  }: {
    children: ReactNode;
    className?: string;
  }) => (
    <div className={classNames('px-4 d-grid gap-2 pb-4 ', className)}>
      {children}
    </div>
  ),
  FeaturesList: ({ children, ...props }: LayerFeaturesListProps) => (
    <LayerFeaturesList {...props}>{children}</LayerFeaturesList>
  ),
};
